/**
 * The dialects a stored descriptor may be written in, and how one is read.
 *
 * Anything the engine compiles states the dialect it was authored in, in the root of its own
 * document, so the version travels with the content: a preset carries its own into the layout it is
 * merged into, and a document copied into a script or another installation keeps it.
 */

/** What is versioned, and who reads it. */
export const DIALECTS = { LAYOUT: 'layout', DESCRIPTOR: 'descriptor' };

/**
 * The dialect this build speaks, per kind.
 *
 * `layout` is what the render engine compiles - saved layouts and the presets placed in them.
 * `descriptor` is what declares a section's fields, read by the procedures and, in time, by whatever
 * makes them configurable.
 */
export const DIALECT_VERSIONS = { layout: 1, descriptor: 1 };

/** What a document calls the dialect it was written in. */
export const DIALECT_KEY = 'dialect';

/** What a layout document calls its content. */
export const LAYOUT_KEY = 'layout';

/**
 * What each step of the chain does to one region, keyed by the dialect it arrives at.
 *
 * A region stating dialect 1 read by a build speaking 3 is put through 2 and then 3. Empty while
 * there is only one dialect; the first bump adds a function here and nothing else.
 */
export const LAYOUT_MIGRATIONS = {};

/**
 * A layout document as its dialect and its content.
 *
 * @param {*} document - The parsed document, or a checked-in default.
 *
 * @returns {Object|null} `{ dialect, layout }`, or null where it is neither.
 */
export const readStored = (document) => {
  if (!document || typeof document !== 'object' || Array.isArray(document))
    return null;

  const layout = document[LAYOUT_KEY];
  const dialect = Number(document[DIALECT_KEY]);

  if (!Array.isArray(layout) || !Number.isFinite(dialect)) return null;

  return { dialect, layout };
};

/**
 * A checked-in layout, declared at the dialect of the build it ships in.
 *
 * The defaults are code rather than data, but they say so in the same shape a saved one does, so
 * there is one document shape and nothing has to guess which it is holding.
 *
 * @param {Array} layout - The regions, or the entries, the default holds.
 *
 * @returns {Object}
 */
export const atDialect = (layout) => ({
  [DIALECT_KEY]: DIALECT_VERSIONS.layout,
  [LAYOUT_KEY]: layout,
});

/**
 * Whether this build can read a dialect, and what to say where it cannot.
 *
 * A dialect ahead of the build's is read anyway: something that refuses to draw says nothing about
 * why, where one that draws imperfectly at least shows what it is.
 *
 * @param {String} dialect - Which dialect, as `DIALECTS` names them.
 * @param {Number} version - The version the document states.
 *
 * @returns {Object} `{ ok, newer, from, to }`.
 */
export const readableAs = (dialect, version) => {
  const to = DIALECT_VERSIONS[dialect];
  const from = Number.isFinite(version) ? version : to;

  return { ok: from <= to, newer: from > to, from, to };
};

/**
 * One region put through every step between the dialect it states and this build's.
 *
 * @param {Object} region - The region, or the entry, as stored.
 * @param {Number} from - The dialect it was written in.
 *
 * @returns {Object}
 */
const migrateRegion = (region, from) => {
  const to = DIALECT_VERSIONS.layout;
  let held = region;

  for (let step = from + 1; step <= to; step += 1) {
    const migration = LAYOUT_MIGRATIONS[step];

    if (migration) held = migration(held);
  }

  return held;
};

/**
 * Every region of a layout brought up to this build's dialect.
 *
 * A region filled in from a preset states the dialect that preset was written in, which is not
 * necessarily the layout's: a preset is placed for years and the layout around it may be newer. So
 * each region is migrated from where it came from rather than from where the layout says it is.
 *
 * @param {Array} regions - The layout's content.
 * @param {Number} from - The dialect the layout states, for the regions stating none.
 *
 * @returns {Array}
 */
export const migrateRegions = (regions, from) => {
  const to = DIALECT_VERSIONS.layout;
  const steps = Object.keys(LAYOUT_MIGRATIONS).length;

  if (steps === 0) return regions;

  return (regions ?? []).map((region) => {
    const stated = Number(region?.[DIALECT_KEY]);
    const at = Number.isFinite(stated) ? stated : from;

    return at >= to ? region : migrateRegion(region, at);
  });
};
